<template>
	<div class="tour-wrapper">
		<b-modal :active.sync="tourModalActive" :width="640" scroll="keep">
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">Welcome to Griptiller</p>
				</header>
				<section class="modal-card-body">
					<p class="is-title">Your broker has organized the properties and reports for you.</p>
					<p>Click through this tour so you don’t miss additional benefits.</p>
				</section>
				<footer class="modal-card-foot">
					<div>
						<button class="button" type="button" @click="skipTour">Skip Tour</button>
					</div>
					<div>
						1 of {{ stepCount }} slides
					</div>
					<div>
						<b-button type="is-info" label="Start Tour" @click.prevent="startTour" />
					</div>
				</footer>
			</div>
		</b-modal>
		<v-tour name="accountTour" :steps="steps">
			<template slot-scope="tour">
				<transition name="fade">
					<v-step
						v-if="tour.currentStep === index"
						v-for="(step, index) of tour.steps"
						:key="index"
						:step="step"
						:previous-step="tour.previousStep"
						:next-step="tour.nextStep"
						:stop="tour.stop"
						:is-first="tour.isFirst"
						:is-last="tour.isLast"
						:labels="tour.labels"
					>
						<template v-if="tour.currentStep === 0">
							<div slot="actions" class="v-step__buttons">
								<button @click="tour.stop" class="v-step__button v-step__button-skip" style="width: 30%">Skip Tour</button>
								<button @click="backToModal" class="v-step__button v-step__button-previous" style="width: 30%">Previous</button>
								<button @click="tour.nextStep" class="v-step__button v-step__button-next" style="width: 30%">Next</button>
							</div>
						</template>
						<template v-if="tour.currentStep === tour.steps.length - 1">
							<div slot="actions" class="v-step__buttons">
								<button @click="backToBeginning" class="v-step__button v-step__button-skip" style="width: 45%">Back to Beginning</button>
								<button @click="backToModal" class="v-step__button v-step__button-previous" style="width: 25%">Previous</button>
								<button @click="tour.stop" class="v-step__button v-step__button-next" style="width: 20%">Finish</button>
							</div>
						</template>
					</v-step>
				</transition>
			</template>
		</v-tour>
	</div>
</template>

<script>
import Cookies from 'js-cookie'
import { mapState, mapGetters } from 'vuex'

export default {

  computed: {
    ...mapState({
      tourModalActive: state => state.account.tourModalActive
    }),
    ...mapGetters({
      userCan: 'userCan'
    }),
    stepCount () {
      let count = 2

      if (this.userCan('view_download_document_list_button', 'accounts') || this.userCan('view_download_property_list_button', 'accounts') || this.userCan('view_download_all_files_button', 'accounts')) ++count

      if (this.userCan('view_download_document_list_button', 'accounts') || this.userCan('view_download_property_list_button', 'accounts') || this.userCan('view_download_all_files_button', 'accounts')) ++count

      if (this.userCan('view_property_types_pie_metric', 'accounts') || this.userCan('create_account_properties', 'account_properties') || this.userCan('bulk_upload_account_properties', 'account_properties') || this.userCan('view_account_reconciliation_metric', 'accounts') || this.userCan('view_property_types__metric', 'accounts') || this.userCan('view_property_locations_metric', 'accounts')) ++count

      if (this.userCan('view_account_reconciliation_metric', 'accounts')) ++count

      if (this.userCan('view_account_properties', 'account_properties')) ++count

      if (this.userCan('view_account_files', 'account_files')) ++count

      return count
    },
    steps () {
      let steps = []
      let stepNumber = 2

      if (this.userCan('view_download_document_list_button', 'accounts') || this.userCan('view_download_property_list_button', 'accounts') || this.userCan('view_download_all_files_button', 'accounts')) {
        steps.push({
          target: '#account-downloads-wrapper',
          content: `
            <div class="tour-step">
              <p class="step-title">
                <span class="tour-step-progress">${stepNumber}/${this.stepCount}</span>
                <span>Download Everything</span>
              </p>
              <ul>
                <li>A handy Disclosed Document List</li>
                <li>A Property List</li>
                <li>One zip file containing All Documents in labeled folders.</li>
              </ul>
            </div>
          `,
          params: {
            placement: 'left'
          }
        })
        ++stepNumber
      }

      if (this.userCan('view_property_types_pie_metric', 'accounts') || this.userCan('create_account_properties', 'account_properties') || this.userCan('bulk_upload_account_properties', 'account_properties') || this.userCan('view_account_reconciliation_metric', 'accounts') || this.userCan('view_property_types__metric', 'accounts') || this.userCan('view_property_locations_metric', 'accounts')) {
        steps.push({
          target: '#account-overview-tab',
          content: `
              <div class="tour-step">
                <p class="step-title">
                  <span class="tour-step-progress">${stepNumber}/${this.stepCount}</span>
                  <span>Overview Tab</span>
                </p>
                <p>Properties are located on a world map, occupancy types are displayed, and data discrepancies are identified.</p>
              </div>
            `,
          params: {
            placement: 'top'
          }
        })
        ++stepNumber
      }

      if (this.userCan('view_account_reconciliation_metric', 'accounts')) {
        steps.push({
          target: '#counts-metric',
          content: `
              <div class="tour-step">
                <p class="step-title"><span class="tour-step-progress">${stepNumber}/${this.stepCount}</span><span>Discrepancies</span></p>
                <p>Each metric identifies common data discrepancies. Use the metrics to work out issues with your transaction partners. Click on any metric to see printable lists.</p>
              </div>
            `,
          params: {
            placement: 'top'
          }
        })
        ++stepNumber
      }

      if (this.userCan('view_account_properties', 'account_properties')) {
        steps.push({
          target: '#account-properties-tab',
          content: `
              <div class="tour-step">
                <p class="step-title"><span class="tour-step-progress">${stepNumber}/${this.stepCount}</span><span>Properties Tab</span></p>
                <p>Click on this tab to research each property.</p>
                <ul>
                  <li>Click on any property name or address to reveal a live Google map.</li>
                  <li>Click on the report title or eyeball icon to read all documents associated with that property.</li>
                </ul>
              </div>
            `,
          params: {
            placement: 'top'
          }
        })
        ++stepNumber
      }

      if (this.userCan('view_account_files', 'account_files')) {
        steps.push({
          target: '#account-files-tab',
          content: `
              <div class="tour-step">
                <p class="step-title"><span class="tour-step-progress">${stepNumber}/${this.stepCount}</span><span>Documents Tab</span></p>
                <p>Click on this tab to see newly added documents by date. Then use the Action button to download selected documents.</p>
              </div>
            `,
          params: {
            placement: 'top'
          }
        })
        ++stepNumber
      }

      steps.push({
        target: '.toggle-notifications',
        content: `
            <div class="tour-step">
              <p class="step-title"><span class="tour-step-progress">${stepNumber}/${this.stepCount}</span><span>Notifications</span></p>
              <p>This notification bell will indicate when new documents or properties are added to the account.</p>
            </div>
          `,
        params: {
          placement: 'bottom'
        }
      })
      ++stepNumber

      steps.push({
        target: '.purechat-button-expand',
        content: `
            <div class="tour-step">
              <p class="step-title"><span class="tour-step-progress">${stepNumber}/${this.stepCount}</span><span>Live Chat</span></p>
              <p>Ask Griptiller staff a quick question.</p>
            </div>
          `,
        params: {
          placement: 'bottom'
        }
      })

      return steps
    }
  },

  methods: {

    startTour () {
      this.$store.commit('hideTourModal')
      this.$tours['accountTour'].start()
    },

    skipTour () {
      this.$store.commit('hideTourModal')
      this.setCookie()
    },

    setCookie () {
      Cookies.set('seenTour', true)
    },

    backToModal () {
      this.$tours['accountTour'].stop()
      this.$store.commit('showTourModal')
    },

    backToBeginning () {
      this.$tours['accountTour'].stop()
      this.$store.commit('showTourModal')
    }

  }

}
</script>



// WEBPACK FOOTER //
// src/components/Account/Tour.vue